import { motion } from "framer-motion";
import { Dumbbell, CalendarDays, TrendingUp, Trophy } from "lucide-react";

const features = [
  {
    icon: Dumbbell,
    title: "Workout Logging",
    description:
      "Log every exercise, set, rep, and weight. Build your own routines or follow a weekly split. Your entire training history, always at your fingertips.",
  },
  {
    icon: CalendarDays,
    title: "Habit Tracker Heatmap",
    description:
      "See your consistency at a glance. A GitHub-style heatmap shows every day you showed up. Build streaks that make skipping feel impossible.",
  },
  {
    icon: TrendingUp,
    title: "Progress Insights",
    description:
      "Track your body weight and strength over time with clean charts. Watch the trend lines move in the right direction week after week.",
  },
  {
    icon: Trophy,
    title: "Personal Records",
    description:
      "FitPulse automatically detects every new PR the moment you hit it. Celebrate wins, big and small, and stay hungry for the next one.",
  },
];

const FeatureCard = ({ feature, index }) => {
  const Icon = feature.icon;
  return (
    <motion.div
      data-testid={`feature-card-${index}`}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      whileHover={{ y: -4 }}
      className="bg-white border border-[#E5E5E5] rounded-2xl p-8 transition-shadow duration-200 hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)] cursor-default"
    >
      <div className="w-12 h-12 bg-[#F5F5F5] rounded-xl flex items-center justify-center mb-5">
        <Icon size={22} className="text-[#0D0D0D]" strokeWidth={1.8} />
      </div>
      <h3 className="text-lg font-semibold text-[#0D0D0D] mb-2">{feature.title}</h3>
      <p className="text-sm text-[#666] leading-relaxed">{feature.description}</p>
    </motion.div>
  );
};

const FeaturesSection = () => {
  return (
    <section
      id="features"
      data-testid="features-section"
      className="bg-white py-20 sm:py-28 lg:py-32"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.5 }}
          className="text-center mb-14"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-[#0D0D0D] tracking-tight mb-3">
            Everything you need. Nothing you don't.
          </h2>
          <p className="text-base text-[#666]">Four powerful tools, one clean app.</p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {features.map((feature, i) => (
            <FeatureCard key={feature.title} feature={feature} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
